import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Spinner, Alert, DropdownButton, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Tasks = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    meetingId: 'all'
  });
  const [meetings, setMeetings] = useState([]);

  // Function to fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser?.id) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/tasks`, {
        params: { userId: currentUser.id }
      });
      
      if (response.data.success) {
        setTasks(response.data.tasks);
      } else {
        setError('Failed to fetch tasks');
      }
      
      // Also fetch meetings for filtering
      const meetingsResponse = await axios.get(`${API_URL}/api/meetings`, {
        params: { userId: currentUser.id }
      });
      
      if (meetingsResponse.data.success) {
        setMeetings(meetingsResponse.data.meetings);
      }
      
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Error fetching tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load tasks on component mount
  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Handle marking a task as complete
  const handleTaskCompletion = async (taskId, completed) => {
    try {
      const response = await axios.put(`${API_URL}/api/tasks/${taskId}`, {
        completed: !completed
      });
      
      if (response.data.success) {
        // Update local state
        setTasks(tasks.map(task => 
          task._id === taskId ? { ...task, completed: !completed } : task
        ));
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Error updating task. Please try again.');
    }
  };

  // Apply filters to tasks
  const filteredTasks = tasks.filter(task => {
    // Filter by status
    if (filters.status !== 'all') {
      if (filters.status === 'completed' && !task.completed) return false;
      if (filters.status === 'pending' && task.completed) return false;
    }
    
    // Filter by priority
    if (filters.priority !== 'all' && task.priority !== filters.priority) return false;
    
    // Filter by meeting
    if (filters.meetingId !== 'all' && task.meeting !== filters.meetingId) return false;
    
    return true;
  });

  // Handle resetting filters
  const resetFilters = () => {
    setFilters({
      status: 'all',
      priority: 'all',
      meetingId: 'all'
    });
  };

  // Get meeting title by ID
  const getMeetingTitle = (meetingId) => {
    const meeting = meetings.find(m => m._id === meetingId);
    return meeting ? meeting.title : 'Unknown Meeting';
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container fluid className="p-3 p-md-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-4">Tasks</h1>
          <p className="text-secondary">
            Manage tasks extracted from your meetings. Check off completed items and filter by status or priority.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Filter Tasks</h5>
          <Row>
            <Col md={3} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select 
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Priority</Form.Label>
                <Form.Select 
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                >
                  <option value="all">All</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Meeting</Form.Label>
                <Form.Select 
                  value={filters.meetingId}
                  onChange={(e) => setFilters({...filters, meetingId: e.target.value})}
                >
                  <option value="all">All Meetings</option>
                  {meetings.map(meeting => (
                    <option key={meeting._id} value={meeting._id}>
                      {meeting.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={resetFilters} className="w-100">
                Reset Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Task List */}
      {filteredTasks.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem', color: '#0d6efd' }}></i>
            <h4 className="mt-3">No Tasks Found</h4>
            <p className="text-secondary">
              {tasks.length === 0 
                ? "You don't have any tasks yet. Tasks are automatically extracted from your meeting transcripts."
                : "No tasks match your current filters. Try adjusting your filters to see more tasks."}
            </p>
            {tasks.length === 0 && (
              <Button 
                variant="primary" 
                className="mt-3"
                onClick={() => navigate('/upload')}
              >
                Process a Meeting
              </Button>
            )}
            {tasks.length > 0 && (
              <Button 
                variant="outline-primary" 
                className="mt-3"
                onClick={resetFilters}
              >
                Show All Tasks
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {filteredTasks.map(task => (
            <Col md={6} lg={4} key={task._id} className="mb-4">
              <Card className={`h-100 ${task.completed ? 'border-success' : ''}`}>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-3">
                    <Badge bg={task.priority === 'high' ? 'danger' : (task.priority === 'medium' ? 'warning' : 'info')}>
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                    </Badge>
                    <Form.Check 
                      type="switch"
                      id={`task-switch-${task._id}`}
                      checked={task.completed}
                      onChange={() => handleTaskCompletion(task._id, task.completed)}
                      label={task.completed ? "Completed" : "Mark Complete"}
                    />
                  </div>
                  
                  <Card.Title className={task.completed ? 'text-decoration-line-through' : ''}>
                    {task.description}
                  </Card.Title>
                  
                  <Card.Text className="mt-3 text-secondary">
                    <small>
                      <i className="bi bi-calendar-event me-2"></i>
                      {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline'}
                    </small>
                  </Card.Text>
                  
                  <Card.Text className="text-secondary">
                    <small>
                      <i className="bi bi-person me-2"></i>
                      {task.assignee || 'Unassigned'}
                    </small>
                  </Card.Text>
                  
                  <div className="mt-3">
                    <Badge bg="light" className="text-secondary">
                      <i className="bi bi-mic me-1"></i> 
                      From: {getMeetingTitle(task.meeting)}
                    </Badge>
                  </div>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-between border-0 bg-transparent">
                  <small className="text-muted">
                    Created: {new Date(task.createdAt).toLocaleDateString()}
                  </small>
                  <DropdownButton 
                    variant="light"
                    title="Actions"
                    size="sm"
                    align="end"
                  >
                    <Dropdown.Item onClick={() => navigate(`/meetings/${task.meeting}`)}>
                      View Source Meeting
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleTaskCompletion(task._id, task.completed)}>
                      {task.completed ? 'Mark as Pending' : 'Mark as Completed'}
                    </Dropdown.Item>
                  </DropdownButton>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default Tasks;
